import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../config/api';
import {
  adminSectionTitleSx,
  adminSolidBtnSx,
  adminSurfaceCardSx,
} from '../../components/AdminChrome';
import { DOCUMENT } from '../../constants/documentTheme';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 0, bgcolor: DOCUMENT.ivory },
};

export default function PrivatePartnersPanel({ t }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [draft, setDraft] = useState({
    inquiryId: '',
    referringPartnerId: '',
    leadflowLeadId: '',
    ownershipType: 'advisor_owned',
    campaignSource: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/private/partners');
      setReferrals(data.referrals || data.partners || []);
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/admin/private/partners', {
        inquiryId: draft.inquiryId ? Number(draft.inquiryId) : null,
        referringPartnerId: draft.referringPartnerId ? Number(draft.referringPartnerId) : null,
        leadflowLeadId: draft.leadflowLeadId ? Number(draft.leadflowLeadId) : null,
        ownershipType: draft.ownershipType,
        campaignSource: draft.campaignSource || null,
      });
      setDraft({
        inquiryId: '',
        referringPartnerId: '',
        leadflowLeadId: '',
        ownershipType: 'advisor_owned',
        campaignSource: '',
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('adminPrivate.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert> : null}
      <Card sx={{ ...adminSurfaceCardSx, p: 2.5, mb: 3 }} elevation={0}>
        <Typography sx={adminSectionTitleSx}>{t('adminPrivate.addReferral')}</Typography>
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <TextField label={t('adminPrivate.fields.inquiryId')} value={draft.inquiryId} onChange={(e) => setDraft((d) => ({ ...d, inquiryId: e.target.value }))} sx={fieldSx} size="small" />
          <TextField label={t('adminPrivate.fields.partnerId')} value={draft.referringPartnerId} onChange={(e) => setDraft((d) => ({ ...d, referringPartnerId: e.target.value }))} sx={fieldSx} size="small" />
          <TextField label={t('adminPrivate.fields.leadflowId')} value={draft.leadflowLeadId} onChange={(e) => setDraft((d) => ({ ...d, leadflowLeadId: e.target.value }))} sx={fieldSx} size="small" />
          <TextField label={t('adminPrivate.fields.campaignSource')} value={draft.campaignSource} onChange={(e) => setDraft((d) => ({ ...d, campaignSource: e.target.value }))} sx={fieldSx} size="small" />
          <Button disabled={saving} sx={adminSolidBtnSx} onClick={create}>{t('adminPrivate.create')}</Button>
        </Stack>
      </Card>
      {referrals.length === 0 ? (
        <Typography sx={{ color: DOCUMENT.ink3, textAlign: 'center', py: 4 }}>{t('adminPrivate.emptyReferrals')}</Typography>
      ) : (
        <TableContainer sx={adminSurfaceCardSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('adminPrivate.cols.client')}</TableCell>
                <TableCell>{t('adminPrivate.cols.partner')}</TableCell>
                <TableCell>{t('adminPrivate.cols.ownership')}</TableCell>
                <TableCell>{t('adminPrivate.fields.leadflowId')}</TableCell>
                <TableCell>{t('adminPrivate.cols.stage')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.clientName || '—'}</TableCell>
                  <TableCell>{row.partnerName || row.referringPartnerId || '—'}</TableCell>
                  <TableCell>{row.ownershipType}</TableCell>
                  <TableCell>{row.leadflowLeadId || '—'}</TableCell>
                  <TableCell>{row.inquiryStage || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
